import { useDocument } from '@nandorojo/swr-firestore';
import { useRecoilState } from 'recoil';
import { pageSetAtomFamily } from '@atom/setting/pageSetState';
import { AUTH_TYPE, AUTH_TYPE_VALUE } from '@typings/common/authorizationType';
import {
  PAGE_LAYOUT_TYPE,
  PAGE_LAYOUT_TYPE_VALUE,
} from '@typings/pages/pageLayout.interface';
import {
  AUTHOR_AVATAR_TYPE,
  AUTHOR_DISPLAY_TYPE,
  IMG_CLICK_ACTION_VALUE,
  IDocSetting,
} from '@typings/posting/postingSetting.interface';
import {
  PAGE_HASHTAG_TYPE,
  PAGE_HASHTAG_TYPE_VALUE,
} from '@typings/setting/pageTagType.interface';
import { IthemeColorSet } from '@typings/setting/themeColor.interface';
import { useEffect } from 'react';
import { formatPixelValue } from '@utils/formatPixelValue';

function usePageSet({ collectionPath }: { collectionPath: string }): {
  isLoading: boolean;
  docLayout: PAGE_LAYOUT_TYPE_VALUE;
  docWidth: string;
  contentWidth: string;
  docLimit: number;
  docLine: number;
  orderBy: OrderByType;
  orderDir: OrderDirType;
  isHideInfoView: boolean;
  isCommentCollapse: boolean;
  isExtendsModal: boolean;
  allCount: number;
  writeAuthType: AUTH_TYPE_VALUE;
  controlAuthType: AUTH_TYPE_VALUE;
  commentAuthType: AUTH_TYPE_VALUE;
  themeSet: IthemeColorSet | null;
  isSeriesView: boolean;
  hashtagType: PAGE_HASHTAG_TYPE_VALUE;
  contentHeight: string;
  arrowShapeImg: string;
  cardShape: 'default' | '3d';
  isRealtimeEnabled: boolean;
  authorDisplayType: AUTHOR_DISPLAY_TYPE;
  authorAvatarType: AUTHOR_AVATAR_TYPE;
  imgClickAction: IMG_CLICK_ACTION_VALUE;
  isTimelineImgFill: boolean;
  userCountdownCode: string;
  showUserTitle: boolean;
  isFluidLayout: boolean;
  fluidLayoutMaxImageSize: number;
  twoLineRatio: string;
} {
  // Recoil에 캐싱된 데이터를 먼저 읽음 → 리마운트 시 즉시 올바른 값 제공
  const [cachedData, setCachedData] = useRecoilState(
    pageSetAtomFamily(collectionPath),
  );

  // Firestore 실시간 리스너 (SWR 캐시 포함)
  const { data: pageSetData } = useDocument<IDocSetting>(
    `pageSetting/${collectionPath}`,
    { listen: true },
  );

  // Firestore 데이터가 도착하면 Recoil에 저장 (다음 방문 시 즉시 활용)
  useEffect(() => {
    if (pageSetData) {
      setCachedData(pageSetData);
    }
  }, [pageSetData, setCachedData]);


  // Recoil 캐시 우선 → SWR 캐시 순으로 사용 (둘 다 없으면 null)
  const data: IDocSetting | null = cachedData ?? pageSetData ?? null;

  return {
    isLoading: data === null,
    docLayout:
      (data?.docLayout as PAGE_LAYOUT_TYPE_VALUE) || PAGE_LAYOUT_TYPE.ONELINE,
    docWidth: formatPixelValue(data?.docWidth || '980px', '980px'),
    contentWidth: formatPixelValue(data?.contentWidth || 'auto', 'auto'),
    isCommentCollapse: data?.isCommentCollapse ?? false,
    isHideInfoView: data?.isHideInfoView ?? false,
    isExtendsModal: data?.isExtendsModal ?? false,
    isSeriesView: data?.isShowSeries ?? false,
    docLimit: data?.docLimit || 1,
    docLine: data?.docLine || 1,
    writeAuthType:
      (data?.writeAuthType as AUTH_TYPE_VALUE) || AUTH_TYPE.MASTER,
    controlAuthType:
      (data?.controlAuthType as AUTH_TYPE_VALUE) || AUTH_TYPE.MASTER,
    commentAuthType:
      (data?.commentAuthType as AUTH_TYPE_VALUE) || AUTH_TYPE.MEMBER,
    allCount: (data as any)?.[`${collectionPath}IdList`]?.length || 0,
    orderBy: (data?.orderBy as OrderByType) || 'createdAt',
    orderDir: (data?.orderDir as OrderDirType) || 'desc',
    themeSet: data?.themeSet || null,
    hashtagType:
      (data?.hashtagType as PAGE_HASHTAG_TYPE_VALUE) ||
      PAGE_HASHTAG_TYPE.HORIZONTAL,
    contentHeight: data?.contentHeight || '300px',
    arrowShapeImg: (data as any)?.arrowShapeImg || '',
    cardShape: ((data as any)?.cardShape || 'default') as 'default' | '3d',
    isRealtimeEnabled: (data as any)?.isRealtimeEnabled ?? true,
    authorDisplayType:
      ((data as any)?.authorDisplayType || 'user') as AUTHOR_DISPLAY_TYPE,
    authorAvatarType:
      ((data as any)?.authorAvatarType || 'account') as AUTHOR_AVATAR_TYPE,
    imgClickAction: ((): IMG_CLICK_ACTION_VALUE => {
      const stored = (data as any)?.imgClickAction as IMG_CLICK_ACTION_VALUE | undefined;
      if (stored) return stored;
      if ((data as any)?.isOpenInNewTab) return 'openInNewTab';
      return 'open';
    })(),
    isTimelineImgFill: (data as any)?.isTimelineImgFill ?? false,
    userCountdownCode: (data as any)?.userCountdownCode || '',
    showUserTitle: (data as any)?.showUserTitle ?? false,
    isFluidLayout: (data as any)?.isFluidLayout ?? false,
    fluidLayoutMaxImageSize: (data as any)?.fluidLayoutMaxImageSize || 600,
    twoLineRatio: (data as any)?.twoLineRatio || '1fr 1fr',
  };
}

export default usePageSet;
